/**
 * **장수 기술**(Todo10 3단계, 사용자가 고른 권장안): 장수는 기술 칸 하나에 출정 전에 고른 기술을 들고 나간다. 단추는
 * 예전 함성 자리 하나 — 모바일 단추 줄을 늘리지 않는다. 기술마다 레벨 1~5가 있고, 장수 레벨 10마다 기술 점수 1점을
 * 받아 원하는 기술에 넣는다(`skillPointsFor`). 점수가 모자라 다 올릴 수 없으니 장수마다 색이 생긴다.
 *
 * - 함성(예전 그대로가 레벨 1): 둘레 적 사기를 꺾고 둘레 아군에 기세(타격 +%)를 준다.
 * - 방진: 고른 분대가 잠시 덜 아프게 맞고 밀리지 않는다. 대신 느리다 — 버티는 한 수.
 * - 일제사격: 고른 분대의 궁수가 곧바로 쏘고 잠시 화살이 더 아프다 — 부딪치기 전 깎는 한 수.
 *
 * 판정 값이라 명부(`ScenarioConfig.skill`)에 싣고 문 앞에서 거른다(`sanitizeSkill`). 수치는 모두 정수다.
 */
import { fp } from './fixed.ts';

export const Skill = {
  Roar: 'roar',
  Phalanx: 'phalanx',
  Volley: 'volley',
} as const;
export type Skill = (typeof Skill)[keyof typeof Skill];

export const SKILL_ORDER: readonly Skill[] = [Skill.Roar, Skill.Phalanx, Skill.Volley];
/** 시뮬레이션 안의 기술 번호(`World.skillCode`). 순서를 바꾸지 않는다 — 체크섬에 든다. */
export const SKILL_CODE: Record<Skill, number> = { roar: 0, phalanx: 1, volley: 2 };

export const SKILL_LEVEL_MAX = 5;
/** 기술 점수를 주는 장수 레벨 간격. 레벨 99면 9점 — 셋을 다 올리려면 12점이 들어 고르게 된다. */
export const SKILL_POINT_EVERY = 10;

/** 장수 레벨에서 나오는 기술 점수. */
export function skillPointsFor(heroLevel: number): number {
  return Math.max(0, Math.floor(heroLevel / SKILL_POINT_EVERY));
}

/** 레벨마다의 수치. 레벨 1 함성은 예전 함성과 같다(`ROAR_*`의 옛 값) — 기술을 고르지 않은 판은 예전 그대로다. */
export interface SkillParams {
  /** 재사용 대기(틱, 30Hz). */
  readonly cd: number;
  /** 지속(틱). 함성·방진. */
  readonly ticks: number;
  /** 함성: 타격 +%. 방진: 받는 피해 −%. 일제사격: 그 화살 +%. */
  readonly pct: number;
  /** 함성이 닿는 반경(고정소수점). */
  readonly radius: number;
}

/**
 * **셋의 균형**(선택지 균형 묶음, 2026-10-03 — 기준: 셋이 장수 생존 10%p 안). 손 안 대는 판은 명령 대상이 전 부대라
 * 방진은 전군을 지키고 일제사격은 궁수 분대 하나에만 걸린다. 벽(깊이 20, 표준 편성, 시드 90)에서 레벨 1 장수 생존이
 * 함성 28 · 방진 28 · 일제사격 16/90, 레벨 5는 34 · 51 · 22/90이었다. 일제사격을 키우고(화살 +20~60% → +50~110%,
 * 5초 → 7초) 방진은 레벨 1은 그대로 레벨마다 오르는 몫만 줄였다(−5%p·+0.8초 → −3%p·+0.5초). 기술마다 따로 잰 실험에서
 * 바꾼 뒤 레벨 1 28 · 28 · 25, 레벨 5 34 · 36 · 32/90(깊이 22: 71 · 73 · 73, 81 · 76 · 73) — 동료 기술 변경까지 합친 최종
 * 조합은 한두 판 다르다(docs/design.md "선택지 균형"). 세 배로 빨리 쏘게 해 본 일제사격(B)은
 * 레벨 5가 오히려 23/90 — 궁수가 화살을 일찍 다 써 버렸다. 방진의 대기를 늘리는 쪽(B)은 50/90로 그대로였다.
 */
export function skillParams(id: Skill, level: number): SkillParams {
  const k = Math.min(SKILL_LEVEL_MAX, Math.max(1, Math.floor(level))) - 1;
  switch (id) {
    case Skill.Phalanx:
      return { cd: 600, ticks: 150 + 15 * k, pct: 25 + 3 * k, radius: 0 };
    case Skill.Volley:
      return { cd: 600 - 30 * k, ticks: VOLLEY_TICKS, pct: 50 + 15 * k, radius: 0 };
    default:
      return { cd: 600 - 30 * k, ticks: 150, pct: 40 + 5 * k, radius: fp(120 + 10 * k) };
  }
}

/** 방진 동안의 걸음(백분율). 버티는 대신 느리다. */
export const PHALANX_SPEED_PCT = 60;
/**
 * 일제사격이 이어지는 틱(5초). 이 동안 쏘는 화살 모두가 더 아프다. 처음에는 곧바로 쏘는 첫 화살 하나만 세게 했는데,
 * 재 보니(`npm run skill-probe`) 기술을 안 쓴 판과 거의 같았다(깊이 15 장수 생존 20 대 24/30, 레벨 5는 21) — 화살 한
 * 대는 판을 바꾸지 못한다. 5초에서 7초로 늘렸다(선택지 균형, `skillParams`).
 */
export const VOLLEY_TICKS = 210;
/**
 * 일제사격 동안 화살 사이 간격(백분율) — 두 배로 빨리 쏜다. 세기(+40~80%)만 올렸을 때도 판이 거의 그대로였다(적이 다가올 때
 * 써도 깊이 15 장수 생존 20 대 21/30). 표준 편성의 궁수는 적어 세기 몫만으로는 모자라, 빠르기와 세기를 함께 준다.
 */
export const VOLLEY_RATE_PCT = 50;

export interface SkillDef {
  readonly id: Skill;
  readonly name: string;
  readonly hanja: string;
  /** 한 줄 설명(레벨 수치를 넣는다). */
  readonly summary: (level: number) => string;
}

const sec = (ticks: number): string => `${Math.round((ticks / 30) * 10) / 10}초`;

export const SKILLS: readonly SkillDef[] = [
  {
    id: Skill.Roar,
    name: '함성',
    hanja: '喊聲',
    summary: (lv) => {
      const p = skillParams(Skill.Roar, lv);
      return `둘레 ${120 + 10 * (Math.min(SKILL_LEVEL_MAX, Math.max(1, lv)) - 1)}px의 아군 사기를 채우고 ${sec(p.ticks)} 동안 ${p.pct}% 더 세게 친다. 적은 사기가 꺾인다. ${sec(p.cd)}에 한 번.`;
    },
  },
  {
    id: Skill.Phalanx,
    name: '방진',
    hanja: '方陣',
    summary: (lv) => {
      const p = skillParams(Skill.Phalanx, lv);
      return `고른 분대가 ${sec(p.ticks)} 동안 받는 피해 −${p.pct}%, 밀리지 않는다. 대신 걸음이 ${PHALANX_SPEED_PCT}%로 느리다. ${sec(p.cd)}에 한 번.`;
    },
  },
  {
    id: Skill.Volley,
    name: '일제사격',
    hanja: '一齊射擊',
    summary: (lv) => {
      const p = skillParams(Skill.Volley, lv);
      return `고른 분대의 궁수가 곧바로 쏘고, ${sec(p.ticks)} 동안 두 배로 빨리 쏘며 화살이 ${p.pct}% 더 아프다. ${sec(p.cd)}에 한 번.`;
    },
  },
];

export const SKILL_BY_ID: ReadonlyMap<string, SkillDef> = new Map(SKILLS.map((s) => [s.id, s]));

/** 명부의 기술. 모르는 기술이면 함성, 레벨은 1~5 정수로 접는다. 없으면 함성 레벨 1(예전 판). */
export function sanitizeSkill(raw: unknown): { id: Skill; level: number } {
  const r = raw && typeof raw === 'object' ? (raw as { id?: unknown; level?: unknown }) : {};
  const id = typeof r.id === 'string' && SKILL_BY_ID.has(r.id) ? (r.id as Skill) : Skill.Roar;
  const lv = typeof r.level === 'number' && Number.isInteger(r.level) ? Math.min(SKILL_LEVEL_MAX, Math.max(1, r.level)) : 1;
  return { id, level: lv };
}
